import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
// Removed dropdown menu imports as we're using inline buttons instead
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Menu, Bell, Settings, LogOut, User, Sun, Moon, LifeBuoy } from "lucide-react";
import { useBadgeCounts } from "@/hooks/useBadgeCounts";

interface HeaderProps {
  onMenuToggle: () => void;
}

export function Header({ onMenuToggle }: HeaderProps) {
  const { currentTheme, updateAppearanceMode } = useTheme();
  const { isAuthenticated, user, logout, isLoggingOut } = useAuth();
  const { counts, loading: badgeLoading, refreshCounts } = useBadgeCounts(30000, isAuthenticated);

  // Debug: Log authentication state
  console.log("Header - isAuthenticated:", isAuthenticated);
  console.log("Header - user:", user);
  console.log("Header - localStorage token:", localStorage.getItem("admin-token"));

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      logout();
    }
  };

  const handleLogin = () => {
    window.location.href = "/landing";
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className="sticky top-0 z-30 flex items-center h-16 gap-4 px-4 border-b bg-background sm:px-6">
      {/* Mobile menu button */}
      <Button variant="ghost" size="sm" onClick={onMenuToggle} className="lg:hidden">
        <Menu className="w-5 h-5" />
      </Button>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Notifications */}
      <Link to="/notifications">
        <Button
          variant="ghost"
          size="sm"
          className="relative"
          onClick={refreshCounts}
          disabled={badgeLoading}
        >
          <Bell className="w-5 h-5" />
          {counts.notifications > 0 && (
            <Badge
              className="absolute w-5 h-5 p-0 text-xs rounded-full -top-1 -right-1"
              style={{ backgroundColor: currentTheme.colors.primary }}
            >
              {counts.notifications > 99 ? "99+" : counts.notifications}
            </Badge>
          )}
        </Button>
      </Link>

      {/* Support Tickets */}
      <Link to="/help-support">
        <Button
          variant="ghost"
          size="sm"
          className="relative"
          onClick={refreshCounts}
          disabled={badgeLoading}
        >
          <LifeBuoy className="w-5 h-5" />
          {counts.supportTickets > 0 && (
            <Badge
              className="absolute w-5 h-5 p-0 text-xs rounded-full -top-1 -right-1"
              style={{ backgroundColor: "#ef4444" }}
            >
              {counts.supportTickets > 99 ? "99+" : counts.supportTickets}
            </Badge>
          )}
        </Button>
      </Link>

      {/* Appearance Mode */}

      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          updateAppearanceMode(currentTheme.isDarkMode ? "light" : "dark");
        }}
      >
        {currentTheme.isDarkMode ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
      </Button>

      {/* User Info and Logout - Alternative to Dropdown */}
      {isAuthenticated ? (
        <div className="flex items-center gap-3">
          {/* User Info */}
          <div className="flex items-center gap-2">
            <Avatar className="w-8 h-8">
              <AvatarImage src="/avatars/01.png" alt="Admin" />
              <AvatarFallback
                style={{ backgroundColor: currentTheme.colors.primary, color: "white" }}
              >
                {getInitials(user?.fullName || "Admin User")}
              </AvatarFallback>
            </Avatar>
            <div className="hidden sm:block">
              <p className="text-sm font-medium leading-none">{user?.fullName || "Admin User"}</p>
              <p className="text-xs text-muted-foreground">
                {user?.email || "admin@snapshift.com"}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1">
            {/* Profile Button */}
            <Button variant="ghost" size="sm" title="Profile">
              <User className="w-4 h-4" />
            </Button>

            {/* Settings Button */}
            <Link to="/settings">
              <Button variant="ghost" size="sm" title="Settings">
                <Settings className="w-4 h-4" />
              </Button>
            </Link>

            {/* Logout Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              disabled={isLoggingOut}
              title="Logout"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4" />
              {isLoggingOut && <span className="ml-1 text-xs">Logging out...</span>}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleLogin}
            style={{
              borderColor: currentTheme.colors.primary,
              color: currentTheme.colors.primary,
            }}
          >
            Sign In
          </Button>
          {/* Temporary test login button - Remove in production */}
          {process.env.NODE_ENV === "development" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                // Simulate login for testing
                const testToken = "test-token-" + Date.now();
                localStorage.setItem("admin-token", testToken);
                window.location.reload();
              }}
            >
              Test Login
            </Button>
          )}
        </div>
      )}
    </header>
  );
}
